import React, {useContext} from 'react';
import glamorous from 'glamorous';
import {CHARCOAL, LIGHT_SHADED_BLUE, WHITE} from '../../../shared/style/colors';
import {BASE_TEXT, WEIGHT, FONTS} from '../../../shared/style/typography';
import {ALPHA} from '../../../shared/style/color-utils';
import CompaniesUsingStackshare from '../../../shared/library/companies-using-stackshare';
import {LAPTOP, PHONE, TABLET} from '../../../shared/style/breakpoints';
import {MobileContext} from '../../../shared/enhancers/mobile-enhancer';
import Cta from './cta';

const Container = glamorous.div({
  position: 'relative',
  width: '100%',
  backgroundColor: ALPHA(LIGHT_SHADED_BLUE, 0.03),
  marginTop: 45,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-evenly',
  paddingTop: 108,
  [PHONE]: {
    margin: '50px 0',
    textAlign: 'center',
    paddingBottom: 550
  }
});

const List = glamorous.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gridTemplateRows: 'repeat(2,1fr)',
  gap: '60px 83px',
  paddingBottom: 697,
  [TABLET]: {
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'repeat(4,1fr)',
    rowGap: 32
  },
  [PHONE]: {
    width: '85%'
  }
});

const Title = glamorous.h3({
  ...BASE_TEXT,
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  fontSize: 24,
  fontWeight: WEIGHT.ULTRA_BLACK,
  lineHeight: '44px',
  color: CHARCOAL,
  marginTop: 0,
  marginBottom: 5,
  [PHONE]: {
    marginBottom: 16,
    marginTop: 0,
    fontSize: 20,
    lineHeight: '37px'
  }
});

const Description = glamorous.p({
  ...BASE_TEXT,
  fontFamily: `${FONTS.CEREBRI_SANS_PRO} !important`,
  fontSize: 24,
  fontWeight: WEIGHT.NORMAL,
  lineHeight: '44px',
  color: CHARCOAL,
  margin: 0,
  [PHONE]: {
    fontSize: 20,
    lineHeight: '37px',
    padding: 0
  }
});

const FeatureTitle = glamorous.h2({
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  fontSize: 30,
  fontWeight: WEIGHT.ULTRA_BLACK,
  lineHeight: '44px',
  marginBottom: 40,
  textAlign: 'center',
  marginTop: 0,
  [TABLET]: {
    marginBottom: 46
  },
  [PHONE]: {
    fontSize: 24,
    marginBottom: 57,
    lineHeight: '37px'
  }
});

const Features = glamorous.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 20px',
  [PHONE]: {
    padding: 0
  }
});

const Item = glamorous.div({
  maxWidth: 500,
  [PHONE]: {
    textAlign: 'left'
  }
});

const ImproveContainer = glamorous.div({
  position: 'absolute',
  top: '62%',
  display: 'flex',
  flexDirection: 'column',
  [LAPTOP]: {
    top: '66%',
    justifyItems: 'center'
  },
  [PHONE]: {
    width: '85%',
    justifyItems: 'center',
    top: '57%'
  }
});

const ImproveItems = glamorous.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 525px)',
  gap: '35px 42px',
  [LAPTOP]: {
    gridTemplateColumns: '1fr',
    gap: '20px 0'
  },
  [PHONE]: {
    gridTemplateColumns: '1fr',
    gap: '20px 0'
  }
});

const ImproveTitle = glamorous.h3({
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  fontSize: 24,
  fontWeight: WEIGHT.ULTRA_BLACK,
  lineHeight: '44px',
  marginTop: 11,
  [PHONE]: {
    marginBottom: 24,
    textAlign: 'left',
    fontSize: 20
  }
});

const ImproveContent = glamorous.p({
  fontFamily: `${FONTS.CEREBRI_SANS_PRO} !important`,
  fontSize: 24,
  fontWeight: WEIGHT.NORMAL,
  lineHeight: '44px',
  margin: 0,
  [PHONE]: {
    textAlign: 'left',
    fontSize: 20
  }
});

const ImproveElement = glamorous.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  background: WHITE,
  boxShadow: '0px 5px 44.9826px rgba(0, 0, 0, 0.1)',
  borderRadius: 10,
  padding: '56px 60px',
  [PHONE]: {
    padding: '32px 30px'
  }
});

const ImproveImage = glamorous.img({
  width: 55,
  height: 55
});

const ImproveHeader = glamorous.h3({
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  fontSize: 30,
  fontWeight: WEIGHT.ULTRA_BLACK,
  lineHeight: '44px',
  margin: 0,
  marginBottom: 57,
  textAlign: 'center',
  [PHONE]: {
    fontSize: 24,
    lineHeight: '37px'
  }
});

const featureList = [
  {
    title: 'Enterprise Architects',
    description: 'Reduce duplication of effort and consolidate technologies to better manage risk.'
  },
  {
    title: 'Developers',
    description: 'See how others have solved problems and choose the best tech for your project.'
  },
  {
    title: 'Engineering Managers',
    description: 'Empower your team to move faster, stay productive, and pay down tech debt.'
  },
  {
    title: 'CTOs/CIOs',
    description: 'Execute your technical vision across your organization.'
  }
];

const improvements = [
  {
    icon: 'https://img.stackshare.io/fe/landing/lock_icon.svg',
    title: 'Improve governance',
    description: 'Use a dynamic tech radar to set up guard rails'
  },
  {
    icon: 'https://img.stackshare.io/fe/landing/rocket_icon.svg',
    title: 'Ship faster',
    description: 'Reduce tech sprawl and speed up dev cycles'
  },
  {
    icon: 'https://img.stackshare.io/fe/private/tech-debt-icon.svg',
    title: 'Shrink tech debt',
    description: 'Get visibility into versions and overlapping tech'
  },
  {
    icon: 'https://img.stackshare.io/fe/landing/risk.svg',
    title: 'Reduce risk',
    description: 'Consolidate versions/tools to reduce attack surface area'
  }
];

const CompaniesUsing = () => {
  const isMobile = useContext(MobileContext);

  return (
    <Container>
      <CompaniesUsingStackshare
        title={'Over 1,000 companies use StackShare Enterprise'}
        customStyle={{
          fontFamily: `${FONTS.CEREBRI_SANS_PRO} !important`,
          fontSize: 30,
          fontWeight: WEIGHT.ULTRA_BLACK
        }}
      />
      <Features>
        <FeatureTitle>StackShare Enterprise is for</FeatureTitle>
        <List>
          {featureList.map((item, index) => (
            <Item key={index}>
              <Title>{item.title}</Title>
              <Description>{item.description}</Description>
            </Item>
          ))}
        </List>
      </Features>
      <ImproveContainer>
        <ImproveHeader>
          {isMobile
            ? 'StackShare Enterprise helps you'
            : 'StackShare Enterprise helps engineering orgs'}
        </ImproveHeader>
        <ImproveItems>
          {improvements.map((element, index) => (
            <ImproveElement key={index}>
              <ImproveImage src={element.icon} />
              <ImproveTitle>{element.title}</ImproveTitle>
              <ImproveContent>{element.description}</ImproveContent>
            </ImproveElement>
          ))}
        </ImproveItems>
        <Cta />
      </ImproveContainer>
    </Container>
  );
};

export default CompaniesUsing;
